import * as React from "react";

import { useSearchParams } from "react-router-dom";
import { exhaustiveSwitch } from "replo-utils/lib/misc";

import { GOOGLE_CLIENT_ID } from "replo-utils/lib/googleAuth";
import {
  buildGoogleOAuthUrl,
  isAllowedReturnOrigin,
  parseOAuthHashResponse,
} from "./googleAuthProxyUtils";

// (Patrick, 2026-03-12): Standalone proxy page for Google OAuth on Vercel
// preview deployments. Preview deployments have dynamic hostnames that can't be
// whitelisted in Google Cloud Console. This page lives at a stable origin
// (dashboard.replo.app) that IS whitelisted, and immediately redirects to
// Google's OAuth consent screen. After the user selects their account, Google
// redirects back here with an ID token in the URL fragment, which is relayed
// to the opener via postMessage.
//
// Flow: popup opens → redirect to Google → user picks account → Google
// redirects back → parse id_token from hash → postMessage to opener → close
//
// Security:
// 1. returnOrigin (passed via OAuth state param) is validated against allowlist
// 2. postMessage uses specific targetOrigin (browser-enforced, never "*")
// 3. The opener validates event.origin matches this page's origin
const GoogleAuthProxy: React.FC = () => {
  const [searchParams] = useSearchParams();
  const [error, setError] = React.useState<string | null>(null);

  // eslint-disable-next-line replo/no-use-effect -- Legacy effect - may or may not be necessary
  React.useEffect(() => {
    const hashResult = parseOAuthHashResponse(window.location.hash);

    exhaustiveSwitch(hashResult)({
      success: (result) => {
        if (window.opener) {
          window.opener.postMessage(
            {
              type: "google-auth-proxy-success",
              credential: result.credential,
              clientId: GOOGLE_CLIENT_ID,
            },
            result.state,
          );
        }
        window.close();
      },
      error: (result) => {
        if (result.state && window.opener) {
          window.opener.postMessage(
            { type: "google-auth-proxy-error", error: result.error },
            result.state,
          );
        }
        window.close();
      },
      invalid_origin: (result) => {
        setError(result.error);
      },
      none: () => {
        // Initial load — validate returnOrigin and redirect to Google OAuth
        const returnOrigin = searchParams.get("returnOrigin");
        if (!returnOrigin || !isAllowedReturnOrigin(returnOrigin)) {
          setError("The return origin is not allowed.");
          return;
        }

        const nonce = crypto.randomUUID();
        window.location.href = buildGoogleOAuthUrl({
          returnOrigin,
          currentOrigin: window.location.origin,
          nonce,
        });
      },
    });
  }, [searchParams]);

  if (error) {
    return (
      <div style={{ padding: 40, fontFamily: "Inter, sans-serif" }}>
        <h2>Invalid Request</h2>
        <p>{error}</p>
      </div>
    );
  }

  return (
    <div
      style={{
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        minHeight: "100vh",
        fontFamily: "Inter, sans-serif",
        color: "#666",
      }}
    >
      <p>Redirecting to Google...</p>
    </div>
  );
};

export default GoogleAuthProxy;
